import {
  DeliveryContainerUtilisationTurnoverDto,
  KnownHistoricalUtilisationTurnoverMetadataGroupBy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import dayjs from 'dayjs';
import { naturalSort } from '../../../core/util/natural-sort.util';
import { sortByLockerSize } from '../../lockers/logic/locker-ui.constants';

const EMPTY_CELL_FILL = '#FFFFFF';

/**
 * Buckets for the heatmap's colour ramp, as the lower bound of each range.
 * Deliberately independent of the capacity chart's bands (which are server-defined
 * range keys) — this ramp is a purely client-side presentation choice.
 */
export const UTILISATION_BANDS = [0, 25, 50, 70, 85, 95, 100] as const;
export type UtilisationBand = (typeof UTILISATION_BANDS)[number];

/**
 * Sequential pastel-blue ramp for the utilisation heatmap. Light at 0%, deep at 100%.
 * Distinct from the capacity chart's green→amber→red palette so the two charts read
 * as different views of the data even at a glance.
 */
export const UTILISATION_BAND_COLOURS: Record<UtilisationBand, string> = {
  0: '#F0F5FA',
  25: '#D6E4F1',
  50: '#B5D0E5',
  70: '#87B5D5',
  85: '#5E94C0',
  95: '#386FA4',
  100: '#1A4E8F'
};

export interface DeliveryContainerUtilisationCell {
  /** ms since epoch — day start, derived from fromDate/toDate iteration */
  dayMs: number;
  /** turnover rate (deliveries per locker per day) — null when the API had no bucket for this day */
  rate: number | null;
  /** rate × 100, rounded to integer for display. null when rate is null. */
  percent: number | null;
  /** Pre-computed colour band — null for missing data */
  band: UtilisationBand | null;
  /** Fully resolved background colour for the cell */
  fill: string;
}

export interface DeliveryContainerUtilisationRow {
  id: string;
  name: string;
  cells: DeliveryContainerUtilisationCell[];
}

export interface DeliveryContainerUtilisationModel {
  days: { ms: number }[];
  rows: DeliveryContainerUtilisationRow[];
}

export function makeDeliveryContainerUtilisationData(
  dto: DeliveryContainerUtilisationTurnoverDto
): DeliveryContainerUtilisationModel {
  const groupBy = dto.metadata.groupBy as KnownHistoricalUtilisationTurnoverMetadataGroupBy;
  const orderedGroups = orderUtilisationGroups(dto.groups, groupBy);

  // Build day list from the metadata window. Bucket coverage may be sparse, so we
  // synthesise a full grid and look up each (group × day) lazily.
  const days = enumerateDays(dto.metadata.fromDate, dto.metadata.toDate);

  const rows: DeliveryContainerUtilisationRow[] = orderedGroups.map((group) => {
    const bucketByDayKey = new Map<string, number | null>();
    group.buckets.forEach((b) => {
      bucketByDayKey.set(dayKey(new Date(b.date)), b.turnoverRate);
    });

    return {
      id: group.id,
      name: group.name,
      cells: days.map(({ ms }) => {
        const rate = bucketByDayKey.get(dayKey(new Date(ms))) ?? null;
        const band = rate == null ? null : bandFor(rate);
        const percent = rate == null ? null : Math.round(rate * 100);
        const fill = band == null ? EMPTY_CELL_FILL : UTILISATION_BAND_COLOURS[band];
        return { dayMs: ms, rate, percent, band, fill };
      })
    };
  });

  return { days, rows };
}

function enumerateDays(fromDate: Date, toDate: Date) {
  const out: { ms: number }[] = [];
  let cursor = dayjs(fromDate).startOf('day');
  const end = dayjs(toDate).startOf('day');
  // Guard against weirdly inverted ranges
  if (end.isBefore(cursor)) return [{ ms: cursor.valueOf() }];
  while (cursor.isBefore(end) || cursor.isSame(end)) {
    out.push({ ms: cursor.valueOf() });
    cursor = cursor.add(1, 'day');
  }
  return out;
}

function dayKey(d: Date) {
  return dayjs(d).format('YYYY-MM-DD');
}

/**
 * Row ordering, matching the legend layout we used for the line variants.
 * - Hub:  natural sort desc (e.g. Hub 9 → Hub 1, so the most-recently-numbered hub is first)
 * - Size: locker-size order (XS, S, M, ...)
 * - None: server order (single group)
 */
function orderUtilisationGroups<G extends { name: string }>(
  groups: G[],
  groupBy: KnownHistoricalUtilisationTurnoverMetadataGroupBy
): G[] {
  switch (groupBy) {
    case KnownHistoricalUtilisationTurnoverMetadataGroupBy.Hub:
      return naturalSort(groups).desc((el) => el.name);
    case KnownHistoricalUtilisationTurnoverMetadataGroupBy.Size:
      return sortByLockerSize(groups, (el) => el.name);
    default:
      return groups;
  }
}

function bandFor(rate: number): UtilisationBand {
  if (rate >= 1.0) return 100;
  if (rate >= 0.95) return 95;
  if (rate >= 0.85) return 85;
  if (rate >= 0.7) return 70;
  if (rate >= 0.5) return 50;
  if (rate >= 0.25) return 25;
  return 0;
}
